import System from './System';
import GameObject from '../GameObject/GameObject';
import { Anim, GetAnimationClip, PlayAnimation } from './Animation/AnimationHelper';
import { SceneTransition } from '../Scene/SceneManager';
import RelativePositionComponent from '../Component/Render/RelativePosition';
import Point from '../Utility/Point';
import Game from '../Game';
import Text from '../Prototype/Text';
import Container from '../Prototype/Container';
import AnimationComponent from '../Component/Animation';
import Sprite from '../Prototype/Sprite';
import { AnimationClipEvent } from './Animation/AnimationClip';
import { GameActions } from '../State/Game/GameActions';
import { AsyncActionStatus } from '../State/AsyncAction';
import { isIOS } from '../Utility/Platform';
import PIXIRenderer from './Render/PIXIRenderer';
import { LoadingScreen } from '../Prototype/Effect/LoadingScreen';
import { IGameState } from '../State/Game/IGameState';
import { scaleSpriteToScreen } from 'Utility/LevelHelper';
import { generifyPromise } from 'Utility/async';
import { setIsTransitioning } from 'Utility/isTransitioning';
import Renderable from 'Component/Renderable';
import { report } from 'report';

export default class TransitionSystem extends System {
    public static LOADING_ANIMATION: string = 'loading_animation';
    public static LOADING_OVERLAY: string = 'loading_overlay';
    public static LOADING_BACKGROUND: string = 'loading_background';
    public container: Container;
    public loadingText: Text;
    public background: Sprite;
    public transitionComplete: boolean = false;
    private loading: LoadingScreen;

    public onStart(): void {
        this.scene.on(SceneTransition.START, () => {
            this.startCoroutine(this.transitionIn());
        });

        this.scene.on(SceneTransition.END, () => {
            this.startCoroutine(this.transitionOut());
        });
    }
    private *transitionIn() {
        try {
            this.transitionComplete = false;
            this.createObjects();
            yield* PlayAnimation(
                GetAnimationClip('ContainerFadeIn', this.container, {
                    startAlpha: 0,
                    endAlpha: 1,
                    duration: 300,
                })
                    .on(AnimationClipEvent.DESTROY, this.transitionInComplete.bind(this, false))
                    .on(AnimationClipEvent.COMPLETE, this.transitionInComplete.bind(this, true))
            );
            yield* this.loading.animate();
        } catch (e) {
            console.error(e);
        }
    }
    private transitionInComplete(success: boolean): void {
        if (this.transitionComplete) return;
        if (success) {
            //Game.scene.getActiveScene().stop();
            this.scene.emit(SceneTransition.STARTED);
        } else {
            const state: IGameState = Game.store.getState();
            GameActions.setState({
                ...state,
                activeScene: {
                    ...state.activeScene,
                    status: AsyncActionStatus.QUEUED,
                },
            });
        }
        this.transitionComplete = true;
    }
    private *transitionOut() {
        try {
            this.loading.stopAnimation();
            yield* generifyPromise(Anim(this.container, { alpha: 0, duration: 500 }));
            this.container.getComponent(Renderable).alpha = 0;
            this.destroyObjects();
            setIsTransitioning(false);
            this.scene.emit(SceneTransition.ENDED);
        } catch (error) {
            this.container?.getComponent(Renderable)?.update({ alpha: 0 });
            report('Failed to transition out of scene.', {
                error,
                once: true,
                sampleRate: 0.1,
                tags: {
                    scene: Game.store.getState().activeScene.result,
                },
            });
        }
    }
    private createObjects() {
        this.container = GameObject.Instantiate(Container, {
            name: 'PreloaderContainer',
            alpha: 0,
        });
        this.container.addComponent(RelativePositionComponent, {
            relativePosition: new Point(0.5, 0.5),
        });

        this.container.addComponent(AnimationComponent);
        this.background = GameObject.Instantiate(Sprite, {
            name: TransitionSystem.LOADING_BACKGROUND,
            texture: 'transition_bg',
            anchor: new Point(0.5, 0.5),
        });

        scaleSpriteToScreen(this.background);

        this.loading = GameObject.Instantiate(LoadingScreen, {});

        this.loadingText = GameObject.Instantiate(Text, {
            text: Game.locale.getText('Loading'),
            style: Game.style.getStyle('loading.text'),
            anchor: new Point(0.45, 0.5),
            position: new Point(
                0,
                isIOS() ? (PIXIRenderer.renderer.width > PIXIRenderer.renderer.height ? 225 : -225) : 225
            ),
        });

        this.loadingText.addComponent(RelativePositionComponent, {
            relativePosition: new Point(0, 0),
        });

        this.container.addChild(this.background);
        this.container.addChild(this.loading);
        this.container.addChild(this.loadingText);

        this.scene.addGameObject(this.container);
    }
    private destroyObjects() {
        this.container.destroy();
    }

    public handleStateChanged(state: IGameState): void {}
    public init(params?: any): void {}
    protected onUpdate(): void {}
    protected onLateUpdate(): void {}
    protected onStop(): void {}
    protected onDestroy(): void {}
    public async preload(): Promise<any> {
        return undefined;
    }
}
